import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import * as ariseDiscord from './arise-discord-api';

import LoadingScreen from '@/components/LoadingScreen';
import useDiscord from '@/components/DiscordAuthFlow/useDiscord';
import Button from '@/components/Forms/Button';
import { IS_DISCORD_ACTIVITY } from '@/globals';

import * as styles from './styles.module.scss';
import { useAppSelector } from '@/state/store';

export interface DiscordAuthFlowMethods {
    authorize: () => void;
    openExternalLink: (url: string) => void;
    completeQuest: () => void;
    getQuestStatus: () => Promise<{ status: string; checked: boolean } | false>;
    setQuestProgress: (progress: number) => void;
    botRegister: () => Promise<void>;
}

const DiscordAuthFlow = forwardRef<DiscordAuthFlowMethods, any>((props, ref) => {
    const { authorize, openExternalLink, user, authError, authDenied, quest, authLoading } = useDiscord();
    const d = useAppSelector((state) => state.discord);

    useImperativeHandle(ref, () => ({
        authorize,
        openExternalLink,
        completeQuest: () => {
            if (quest) {
                quest.complete();
            }
        },
        setQuestProgress: (progress: number) => {
            if (quest) {
                quest.progress(progress);
            }
        },
        getQuestStatus: async () => {
            if (quest) {
                const d = await quest.check();
                return d;
            }
            return false;
        },
        botRegister: async () => {
            let locale = 'en-us';
            try {
                // @ts-ignore
                const userLocale = await window.discordSDK.commands.userSettingsGetLocale();
                if (userLocale.locale) {
                    locale = userLocale.locale;
                }
            } catch (e) {
                console.error('[Arise] Count not get user locale for bot registration', e);
            }

            try {
                await ariseDiscord.botRegister(locale);
            } catch (e) {
                console.error('[Arise] Bot registration failed', e);
            }
        },
    }));

    if (!IS_DISCORD_ACTIVITY && window.location.hostname !== 'localhost') {
        return <></>;
    }

    if (!user || authError) {
        if (authLoading) {
            return <TextOverlay message="Authenticating..." />;
        }

        if (authDenied) {
            return <AuthRequiredOverlay authorise={authorize} />;
        }
        // Removing auth error messaging
        // if (authError) {
        //     return <AuthErrorOverlay authorise={authorize} />;
        // }

        // If there's no user and it's not in any of the other states, show the loader
        return <TextOverlay message="Discord SDK Loading..." />;
    }

    // return (
    //     <>
    //         <div className={styles.info}>{JSON.stringify(d, null, 4)}</div>
    //     </>
    // );
    return <></>;
});

function OverlayWrapper({ children }) {
    return (
        <div className={styles.overlayWrapper}>
            <div className={styles.overlayContentWrapper}>
                <div className={styles.logo}>
                    <LoadingScreen show={true} hasBackground={false} logoImageUrl="/assets/global/preloader/logo.png" />
                </div>
                {children}
            </div>
        </div>
    );
}
function TextOverlay({ message }: { message?: string }) {
    return (
        <OverlayWrapper>
            <div>{message}</div>
        </OverlayWrapper>
    );
}

function AuthRequiredOverlay({ authorise }: { authorise: () => void }) {
    return (
        <OverlayWrapper>
            <div className={styles.authCopy}>Please login with your Discord account to play this experience.</div>
            <Button
                onClick={() => {
                    authorise();
                }}
                className={styles.loginButton}
            >
                Log In
            </Button>
        </OverlayWrapper>
    );
}

function AuthErrorOverlay({ authorise }: { authorise: () => void }) {
    return (
        <OverlayWrapper>
            <div className={styles.authCopy}>Authorization error. Please try logging in again.</div>
            <Button
                onClick={() => {
                    authorise();
                }}
                className={styles.loginButton}
            >
                Try Again
            </Button>
        </OverlayWrapper>
    );
}

export function QuestUnenrolledNotice({ gotoEnroll }: { gotoEnroll?: () => void }) {
    const [collapsed, setCollapsed] = useState(false);
    function renderCollapsed() {
        return (
            <span className={styles.enrolButton} onClick={gotoEnroll}>
                Join Quest
            </span>
        );
    }
    function renderFull() {
        return (
            <div>
                <span>Join the quest to get your reward.</span>
                <span className={styles.enrolButton} onClick={gotoEnroll}>
                    Join Quest (TODO)
                </span>
                <span
                    className={styles.enrolButton}
                    onClick={() => {
                        setCollapsed(!collapsed);
                    }}
                >
                    &lt;
                </span>
            </div>
        );
    }
    return (
        <div className={styles.questUnenrolledNotice + ' ' + (collapsed ? styles.collapsed : '')}>
            {collapsed ? renderCollapsed() : renderFull()}
        </div>
    );
}

export default DiscordAuthFlow;
